import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { ConnectPage } from './pages/ConnectPage';
import { ConnectingPage } from './pages/ConnectingPage';
import { HubActivatePage } from './pages/HubActivatePage';
import { HubLoginPage } from './pages/HubLoginPage';
import { HubAccessPage } from './pages/HubAccessPage';
import { LeadsPage } from './pages/LeadsPage';
import { LeadSnapshotPage } from './pages/LeadSnapshotPage';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { HubLayout } from './layouts/HubLayout';
import { HubOverviewPage } from './pages/hub/HubOverviewPage';
import { HubFindingsPage } from './pages/hub/HubFindingsPage';
import { HubUsersPermissionsPage } from './pages/hub/HubUsersPermissionsPage';
import { HubDataHealthPage } from './pages/hub/HubDataHealthPage';
import { HubGetHelpPage } from './pages/hub/HubGetHelpPage';
import { HubSettingsPage } from './pages/hub/HubSettingsPage';
import { HubAlertsSubscriptionPage } from './pages/hub/HubAlertsSubscriptionPage';
import { HubObjectsPage } from './pages/hub/audit/HubObjectsPage';
import { HubFlowsPage } from './pages/hub/audit/HubFlowsPage';
import { HubTechDebtPage } from './pages/hub/audit/HubTechDebtPage';
import { HubFoundationsPage } from './pages/hub/audit/HubFoundationsPage';
import { HubEmailPage } from './pages/hub/audit/HubEmailPage';
import { HubDataCloudPage } from './pages/hub/audit/HubDataCloudPage';
import { HubLicensesPage } from './pages/hub/audit/HubLicensesPage';
import { HubAppsPage } from './pages/hub/audit/HubAppsPage';
import { HubStoragePage } from './pages/hub/audit/HubStoragePage';
import { ProspectLandingPage } from './pages/prospect/ProspectLandingPage';
import { ProspectPrivacyPage } from './pages/prospect/ProspectPrivacyPage';
import { ScanConsentPage, ExpiredLink } from './pages/prospect/ScanConsentPage';
import { ProspectTeaserPage } from './pages/prospect/ProspectTeaserPage';
import { TeaserPreviewPage } from './pages/prospect/TeaserPreviewPage';
import { fetchMe } from './lib/hubApi';

export function App() {
  return (
    <>
      <Toaster position="bottom-right" />
      <Routes>
      <Route path="/" element={<HomeRouter />} />
      <Route path="/connect" element={<ConnectPage />} />
      <Route path="/hub-login" element={<HubLoginPage />} />
      <Route path="/connecting" element={<ConnectingPage />} />
      <Route path="/activate/:token" element={<HubActivatePage />} />

      {/* Public client-scan pages — no role guards, must render for strangers.
          /scan/:token is where an invite link lands; it is deliberately not
          /connect, which is the consultant sign-in above. */}
      <Route path="/privacy" element={<ProspectPrivacyPage />} />
      <Route path="/scan/:token" element={<ScanConsentPage />} />
      {/* Where the server sends a client whose invite died mid-login. */}
      <Route path="/scan-expired" element={<ExpiredLink />} />
      {/* Retired self-serve funnel (form, email verify, session-gated connect):
          every old link lands on the public page. */}
      <Route path="/verify/*" element={<Navigate to="/" replace />} />
      <Route path="/prospect/connect" element={<Navigate to="/" replace />} />
      <Route path="/prospect/teaser" element={<ProspectTeaserPage />} />
      <Route path="/teaser/:token" element={<ProspectTeaserPage />} />
      {/* Dev-only visual harness for the teaser render (mock data, no OAuth). */}
      {import.meta.env.DEV && (
        <Route path="/prospect/teaser-preview" element={<TeaserPreviewPage />} />
      )}

      {/* Internal is client scans only since TYR-216. The live-session audit,
          the AI report and the export pages are gone, so every other
          /internal/* URL lands on the scan list rather than a dead route. */}
      <Route path="/internal" element={<DashboardLayout />}>
        <Route index element={<Navigate to="/internal/leads" replace />} />
        <Route path="leads" element={<LeadsPage />} />
        <Route path="leads/:id" element={<LeadSnapshotPage />} />
        <Route path="hub-access" element={<HubAccessPage />} />
        <Route path="*" element={<Navigate to="/internal/leads" replace />} />
      </Route>

      {/* Back-compat — /dashboard/* still works for any old bookmarks. */}
      <Route path="/dashboard/*" element={<Navigate to="/internal/leads" replace />} />

      <Route path="/hub" element={<HubLayout />}>
        <Route index element={<HubOverviewPage />} />
        <Route path="findings" element={<HubFindingsPage />} />
        <Route path="users-permissions" element={<HubUsersPermissionsPage />} />
        <Route path="data-health" element={<HubDataHealthPage />} />
        <Route path="audit/objects" element={<HubObjectsPage />} />
        <Route path="audit/flows" element={<HubFlowsPage />} />
        <Route path="audit/tech-debt" element={<HubTechDebtPage />} />
        <Route path="audit/foundations" element={<HubFoundationsPage />} />
        <Route path="audit/email" element={<HubEmailPage />} />
        <Route path="audit/data-cloud" element={<HubDataCloudPage />} />
        <Route path="audit/licenses" element={<HubLicensesPage />} />
        <Route path="audit/apps" element={<HubAppsPage />} />
        <Route path="audit/storage" element={<HubStoragePage />} />
        <Route path="get-help" element={<HubGetHelpPage />} />
        <Route path="settings" element={<HubSettingsPage />} />
        <Route path="settings/alerts" element={<HubAlertsSubscriptionPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

// "/" is the public front door: strangers get the prospect landing page,
// while an authenticated consultant / client-admin still bounces straight
// to their surface. Consultants keep entering via /connect as before.
function HomeRouter() {
  const navigate = useNavigate();
  const [visitor, setVisitor] = useState(false);

  useEffect(() => {
    fetchMe().then((me) => {
      // No session, or a session carrying no role (which since TYR-213 is not
      // a consultant), gets the public landing page rather than a surface it
      // has no grant for.
      if (!me || !me.role) {
        setVisitor(true);
        return;
      }
      navigate(me.role === 'client-admin' ? '/hub' : '/internal/leads', { replace: true });
    });
  }, [navigate]);

  return visitor ? (
    <ProspectLandingPage />
  ) : (
    <div className="min-h-screen flex items-center justify-center bg-surface-0 text-crux-light">
      <p className="opacity-60">Loading…</p>
    </div>
  );
}
